import React, { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, Check, ChevronRight, CircleAlert, Info, ShieldCheck, Workflow, X } from 'lucide-react';
import { loadLaboratoryAcceptanceRules, loadLaboratoryAnalyses } from './api';
import { CategoryGlyph, CheckGlyph, SpecimenGlyph } from './LaboratoryGlyphs';
import { LabEmpty, LabResource, TubeState } from './LaboratoryParts';
import {
  analysisName, categoryName, criterionLimits, criterionText, departmentShort, noteText, requirementView, sampleTypeName,
} from './LaboratoryText';
import { Head, Pill, SearchField, WorkspaceTabs } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-reference.css';

// ------------------------------------------------------------------ analysis catalogue

// One line per sample the analysis accepts. Where the amounts differ by sample
// type inside one row of the list, each type gets its own line.
function sampleLines(entry) {
  const views = entry.requirements.map(requirement => requirementView(entry, requirement));
  if (!views.some(view => view.sampleTypeCode)) return [{ key: entry.id, types: entry.sampleTypes, needs: views }];
  return entry.sampleTypes.map(type => ({ key: `${entry.id}-${type.code}`, types: [type],
    needs: views.filter(view => !view.sampleTypeCode || view.sampleTypeCode === type.code) }));
}

function Need({ need }) {
  if (need.sentence) return <span className="xo-cat-sentence">{need.sentence}</span>;
  return <span className="xo-cat-need"><small>{need.label}</small><strong>{need.value}</strong>{need.condition !== null && <em>{need.condition}</em>}</span>;
}

function AnalysisCard({ analysis, text }) {
  const min = Math.min(...analysis.entries.map(entry => entry.turnaroundWeeks[0]));
  const max = Math.max(...analysis.entries.map(entry => entry.turnaroundWeeks[1]));
  return <section className="xo-cat-card" aria-label={analysis.code}>
    <header>
      <span className="xo-cat-code">{analysis.code}</span>
      <div className="xo-cat-title"><h2>{analysisName(analysis.code)}</h2><small>{departmentShort(analysis.departmentCode)}</small></div>
      <div className="xo-cat-turnaround"><small>{text('turnaround')}</small>
        <p><strong>{min === max ? min : text('weeks_range', { min, max })}</strong>{text('weeks_unit')}</p></div>
    </header>
    <ul className="xo-cat-lines">{analysis.entries.flatMap(sampleLines).map(line => <li key={line.key}>
      <div className="xo-cat-sample"><span className="xo-lab-glyph"><SpecimenGlyph code={line.types[0].code} /></span>
        <strong>{line.types.map(type => sampleTypeName(type.code)).join(', ')}</strong></div>
      <div className="xo-cat-needs">{line.needs.map(need => <Need key={need.key} need={need} />)}</div>
    </li>)}</ul>
    {analysis.notes.length > 0 && <details className="xo-cat-notes">
      <summary><Info aria-hidden="true" />{text('good_to_know')}<ChevronRight aria-hidden="true" /></summary>
      <ul>{analysis.notes.map(note => <li key={note.noteNo}>{noteText(note.document, note.noteNo)}</li>)}</ul>
    </details>}
    <footer>
      {analysis.workflowCode !== null
        ? <Link to={`/xomics/laboratory/workflow-definitions?workflow=${analysis.workflowCode}`}><Workflow aria-hidden="true" />{text('open_workflow')}<ChevronRight aria-hidden="true" /></Link>
        : <span>{text('no_workflow')}</span>}
      {analysis.pipelines.length > 0 && <span className="xo-cat-pipelines" title={text('pipelines_label')}>
        {analysis.pipelines.map(pipeline => <code key={`${pipeline.name}-${pipeline.mode}`}>{pipeline.mode ? `${pipeline.name} (${pipeline.mode})` : pipeline.name}</code>)}</span>}
    </footer>
  </section>;
}

export function XomicsLabAnalyses() {
  const resource = useLiveResource(['laboratory'], loadLaboratoryAnalyses);
  const [params] = useSearchParams();
  const [query, setQuery] = useState(params.get('analysis') || '');
  const [sample, setSample] = useState('');
  const text = (key, values) => tx(`ui.lab.analysis.${key}`, values);
  return <div className="xo-platform-page xo-lab-page">
    <Head title={text('title')} subtitle={text('intro')} />
    <LabResource resource={resource}>{analyses => {
      const types = [...new Map(analyses.flatMap(analysis => analysis.entries.flatMap(entry => entry.sampleTypes)).map(type => [type.code, type])).values()];
      const needle = query.trim().toLowerCase();
      const shown = analyses.map(analysis => ({ ...analysis, entries: analysis.entries.filter(entry => (sample === '' || entry.sampleTypes.some(type => type.code === sample))
        && (needle === '' || `${analysis.code} ${analysisName(analysis.code)} ${entry.sampleTypes.map(type => sampleTypeName(type.code)).join(' ')}`.toLowerCase().includes(needle))) }))
        .filter(analysis => analysis.entries.length > 0);
      return <>
        <div className="xo-lab-catalogue-tools"><SearchField value={query} onChange={setQuery} placeholder={text('search')} /></div>
        <div className="xo-cat-samples" role="group" aria-label={text('sample_filter')}>
          <button type="button" aria-pressed={sample === ''} onClick={() => setSample('')}>{text('all_samples')}</button>
          {types.map(type => <button type="button" key={type.code} aria-pressed={sample === type.code} onClick={() => setSample(sample === type.code ? '' : type.code)}>
            <SpecimenGlyph code={type.code} />{sampleTypeName(type.code)}</button>)}
        </div>
        <div className="xo-cat-grid">{shown.map(analysis => <AnalysisCard key={analysis.code} analysis={analysis} text={text} />)}</div>
        {shown.length === 0 && <LabEmpty />}
      </>;
    }}</LabResource>
  </div>;
}

// ------------------------------------------------------------------ acceptance rules

const KIND_ICONS = { accept: Check, reject: X, conditional: CircleAlert, note: Info };
const ruleCount = count => tx(count === 1 ? 'ui.lab.rules.rule_one' : 'ui.lab.rules.rule_count', { count });

function Rule({ set, criterion }) {
  const text = (key, values) => tx(`ui.lab.rules.${key}`, values);
  return <li>
    <span className="xo-rule-check" title={tx(`ui.lab.rules.check.${criterion.check}`)}><CheckGlyph code={criterion.check} /></span>
    <div><p>{criterionText(set.code, criterion.position)}</p>
      {(criterion.requiresWaiver || criterion.requiresDiscussionRecord || criterion.refuses) && <div className="xo-rule-tags">
        {criterion.requiresWaiver && <Pill tone="warn">{text('tag_waiver')}</Pill>}
        {criterion.requiresDiscussionRecord && <Pill tone="info">{text('tag_discussion')}</Pill>}
        {criterion.refuses && <Pill tone="bad">{text('tag_refused')}</Pill>}
      </div>}</div>
  </li>;
}

function RuleGroup({ set, kind, criteria }) {
  const Icon = KIND_ICONS[kind];
  if (criteria.length === 0) return null;
  return <section className={`xo-rule-group is-${kind}`}>
    <h3><span><Icon aria-hidden="true" /></span>{tx(`ui.lab.accept.rules.${kind}`)}</h3>
    <ul>{criteria.map(criterion => <Rule key={criterion.id} set={set} criterion={criterion} />)}</ul>
  </section>;
}

// The numbers of a sample type's rules, each once.
function figures(criteria) {
  const seen = new Map();
  for (const criterion of criteria) for (const limit of criterionLimits(criterion)) {
    const label = criterion.sampleTypeCode ? `${limit.label} · ${sampleTypeName(criterion.sampleTypeCode)}` : limit.label;
    seen.set(`${limit.kind}-${criterion.sampleTypeCode || ''}-${limit.value}`, { label, value: limit.value });
  }
  return [...seen.entries()].map(([key, figure]) => ({ key, ...figure }));
}

export function XomicsLabAcceptanceRules() {
  const resource = useLiveResource(['laboratory'], loadLaboratoryAcceptanceRules);
  const [params, setParams] = useSearchParams();
  const text = (key, values) => tx(`ui.lab.rules.${key}`, values);
  return <div className="xo-platform-page xo-lab-page">
    <Head title={text('title')} subtitle={text('intro')} />
    <LabResource resource={resource}>{ruleSets => {
      // A corrected set replaces its document; only the rules in force are shown.
      const current = ruleSets.filter(item => item.status === 'current').sort((a, b) => a.code.localeCompare(b.code));
      if (current.length === 0) return <LabEmpty />;
      const set = current.find(item => item.department.code === params.get('lab')) || current[0];
      const category = set.categories.find(item => item.code === params.get('sample')) || set.categories[0];
      const own = set.criteria.filter(criterion => criterion.categoryId === category.id);
      const general = set.criteria.filter(criterion => criterion.categoryId === null);
      const ofKind = (criteria, kind) => criteria.filter(criterion => criterion.kind === kind);
      const usesCatalogue = own.some(criterion => criterion.usesAnalysisList);
      return <>
        {current.length > 1 && <WorkspaceTabs className="xo-rule-labs" value={set.department.code} label={text('title')}
          onChange={code => setParams({ lab: code })} tabs={current.map(item => ({ value: item.department.code, label: departmentShort(item.department.code) }))} />}
        <div className="xo-rule-top">
          <section className="xo-rule-panel">
            <header><h2>{text('checks_title')}</h2><p>{text('checks_detail')}</p></header>
            <ol className="xo-rule-checks">{set.checks.map(check => <li key={check.code} className={check.blocksPlainAccept ? 'is-required' : ''}>
              <span className="xo-lab-glyph is-large"><CheckGlyph code={check.code} /></span>
              <strong>{tx(`ui.lab.accept.check.${check.code}`)}</strong>
              <small>{text(check.blocksPlainAccept ? 'check_required' : 'check_noted')}</small>
            </li>)}</ol>
          </section>
          <section className="xo-rule-panel">
            <header><h2>{text('decisions')}</h2>
              {set.approvalRequirement === 'required' && <Pill tone="warn"><ShieldCheck aria-hidden="true" />{text('approval_required')}</Pill>}</header>
            <ul className="xo-rule-decisions">{set.decisions.map(decision => <li key={decision.code}>
              <strong>{tx(`ui.lab.accept.decision.${decision.code}`)}</strong><ArrowRight aria-hidden="true" /><TubeState value={decision.tubeState} />
            </li>)}</ul>
          </section>
        </div>
        <nav className="xo-rule-tiles" aria-label={text('sample_types')}>{set.categories.map(item => (
          <button type="button" key={item.code} aria-current={item.code === category.code ? 'page' : undefined}
            onClick={() => setParams({ lab: set.department.code, sample: item.code })}>
            <span className="xo-lab-glyph is-large"><CategoryGlyph code={item.code} /></span>
            <strong>{categoryName(item.code)}</strong>
            <small>{ruleCount(set.criteria.filter(criterion => criterion.categoryId === item.id).length)}</small>
          </button>
        ))}</nav>
        <article className="xo-rule-board" aria-label={categoryName(category.code)}>
          <header>
            <span className="xo-lab-glyph is-large"><CategoryGlyph code={category.code} /></span>
            <div className="xo-rule-board-title"><h2>{categoryName(category.code)}</h2>
              <p>{category.sampleTypes.map(type => sampleTypeName(type.code)).join(' · ')}</p></div>
            <div className="xo-rule-figures">
              {figures(own).map(figure => <div key={figure.key}><small>{figure.label}</small><strong>{figure.value}</strong></div>)}
              {usesCatalogue && <Link to="/xomics/laboratory/analyses"><small>{tx('ui.lab.limit.amount')}</small>
                <strong>{text('per_analysis')}<ChevronRight aria-hidden="true" /></strong></Link>}
            </div>
          </header>
          <div className="xo-rule-columns">
            <RuleGroup set={set} kind="accept" criteria={ofKind(own, 'accept')} />
            <RuleGroup set={set} kind="reject" criteria={ofKind(own, 'reject')} />
          </div>
          <RuleGroup set={set} kind="note" criteria={ofKind(own, 'note')} />
        </article>
        {general.length > 0 && <article className="xo-rule-board is-general" aria-label={text('every_sample')}>
          <header><div className="xo-rule-board-title"><h2>{text('every_sample')}</h2><p>{text('every_sample_detail')}</p></div></header>
          <div className="xo-rule-columns">
            <RuleGroup set={set} kind="reject" criteria={ofKind(general, 'reject')} />
            <RuleGroup set={set} kind="conditional" criteria={ofKind(general, 'conditional')} />
          </div>
          <RuleGroup set={set} kind="accept" criteria={ofKind(general, 'accept')} />
          <RuleGroup set={set} kind="note" criteria={ofKind(general, 'note')} />
        </article>}
      </>;
    }}</LabResource>
  </div>;
}
